import { useRef, useState } from 'react';
import { Input, Box, Typography, Button, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { AppSkillId } from '../../../../../../../components/staticComponents/StaticHtmlGenerator';
import { enqueueSnackbar } from 'notistack';
import Api, { ApiError } from '../../../../../../../data/api/Api';
import { isValidUrl, showSnackbarErrorMessage } from '../../../../../../../components/staticComponents/staticUtiles';
import { useAppDispatch, useAppSelector } from '../../../../../../../reduxStore/redux-hooks';
import { updateDatasetState, updateFileSelectionState } from '../../../../../../../reduxStore/slices/FileUploadSlice';
import { currentDataSet, refreshDatasetList } from '../../../../../../../reduxStore/slices/DataSetSlice';
import { LinearProgress } from '@mui/material';
import { SharePointDataset } from '../../../../../../../components/rightPanel/subpanel/dataset/createDataset/existingSharePointdataset/ExistingSharePointDatset';

interface AttachSharepointDatasetProps {
  attachDataset: (datasetId: string) => void;
}

export default function AttachSharepointDataset({ attachDataset }: AttachSharepointDatasetProps) {
  const [siteUrl, setSiteUrl] = useState('');
  const [workflowUrl, setWorkflowUrl] = useState('');
  const [includeSubfolder, setIncludeSubfolder] = useState<boolean>(false);
  const [libraryFolderPath, setLibraryFolderPath] = useState<string[]>([]);
  const [sharePointDatasetName, setSharePointDatasetName] = useState('');
  const [isDatasetCreating, setIsDatasetCreating] = useState(false);
  const [datasetCreated, setDatasetCreated] = useState(false);
  const dispatch = useAppDispatch();
  const { fileSelection } = useAppSelector((state) => state.fileUpload);
  const { t } = useTranslation();
  const [selectedSharepointDataset, setSelectedSharepointDataset] = useState<SharePointDataset>();

  const datasetIdRef = useRef<string>('');

  const fetchAndUpdateSharepointDatasetSelection = async (currentDatasetID: string) => {
    try {
      const sharedDatasetResponse = (await (
        await Api.listDatasets(true, false, AppSkillId.sharePoint)
      ).data) as SharePointDataset[];
      const selectedDataset = sharedDatasetResponse.find((dataset) => dataset._id === currentDatasetID);
      setSelectedSharepointDataset(selectedDataset);
    } catch (error) {
      showSnackbarErrorMessage(error as ApiError);
    }
  };

  const createSharePointDocument = async () => {
    if (!isValidUrl(siteUrl)) {
      enqueueSnackbar('Please enter a valid Site URL.', { variant: 'error' });
      return;
    }

    if (!isValidUrl(workflowUrl)) {
      enqueueSnackbar('Please enter a valid Workflow URL.', { variant: 'error' });
      return;
    }
    setIsDatasetCreating(true);
    try {
      const sharepoint_data = { site_url: siteUrl, power_automate_workflow_url: workflowUrl, include_subfolder: includeSubfolder, library_folder_path: libraryFolderPath };
      const createDataset = await Api.createDataset(sharePointDatasetName, AppSkillId.sharePoint, false, sharepoint_data);
      datasetIdRef.current = createDataset.data._id;
      dispatch(updateDatasetState({ datasetName: sharePointDatasetName }));
      dispatch(currentDataSet({ currentDataSetName: sharePointDatasetName, currentDataSetId: datasetIdRef.current }));

      const prepare = await Api.prepareDataset(datasetIdRef.current, AppSkillId.DocCompletion);
      
      // All SharePoint datasets now stop after prepareDataset call (General purpose dataset behavior)
      // No more dataset type selection - always show queued modal
      {
        console.log('🚫 SharePoint document upload prevention for General purpose dataset - removing workflow URL');
        
        // CRITICAL: Immediately disable SharePoint workflow to prevent further document uploading
        try {
          await Api.updateSharePointDataset(
            datasetIdRef.current, 
            sharePointDatasetName, 
            AppSkillId.sharePoint, 
            {
              site_url: siteUrl,
              power_automate_workflow_url: '', // Remove workflow URL to stop automation
            }
          );
          console.log('✅ Workflow URL removed - SharePoint automation stopped');
          
          // ENHANCED: Multiple checks at different intervals to catch any delayed uploads
          const checkAndRemoveDocuments = async (attempt: number) => {
            try {
              console.log(`🔍 Checking for documents (attempt ${attempt})...`);
              const datasetInfo = await Api.getDataset(datasetIdRef.current);
              const dataset = datasetInfo.data as any;
              
              if (dataset && dataset.files && dataset.files.length > 0) {
                console.log(`⚠️ ALERT: Found ${dataset.files.length} uploaded documents despite prevention - removing immediately!`);
                
                // Immediately delete dataset and recreate
                await Api.deleteDataset(datasetIdRef.current);
                console.log('🗑️ Deleted dataset with uploaded documents');
                
                // Recreate completely clean dataset
                const cleanSharepointData = { 
                  site_url: siteUrl, 
                  power_automate_workflow_url: '', // No workflow URL from the start
                  include_subfolder: includeSubfolder, 
                  library_folder_path: libraryFolderPath 
                };
                
                const recreateDataset = await Api.createDataset(
                  sharePointDatasetName, 
                  AppSkillId.sharePoint, 
                  false, 
                  cleanSharepointData
                );
                datasetIdRef.current = recreateDataset.data._id;
                
                // Prepare the clean dataset (no workflow URL = no uploads)
                await Api.prepareDataset(datasetIdRef.current, AppSkillId.DocCompletion);
                
                console.log('✅ Dataset recreated without SharePoint documents - ID:', datasetIdRef.current);
                return true; // Found and removed documents
              } else {
                console.log(`✅ No documents found in attempt ${attempt}`);
                return false; // No documents found
              }
            } catch (checkError) {
              console.warn(`Warning: Document check attempt ${attempt} failed:`, checkError);
              return false;
            }
          };
          
          // Check immediately
          const found1 = await checkAndRemoveDocuments(1);
          
          // Wait and check again after 2 seconds
          await new Promise(resolve => setTimeout(resolve, 2000));
          const found2 = await checkAndRemoveDocuments(2);
          
          // Wait and check again after 5 seconds 
          await new Promise(resolve => setTimeout(resolve, 3000));
          const found3 = await checkAndRemoveDocuments(3);
          
          // Final check after 10 seconds
          await new Promise(resolve => setTimeout(resolve, 5000));
          const found4 = await checkAndRemoveDocuments(4);
          
          if (!found1 && !found2 && !found3 && !found4) {
            console.log('✅ SUCCESS: No SharePoint documents uploaded - prevention worked!');
          } else {
            console.log('⚠️ Documents were found and removed - check logs above for details');
          }
        } catch (cleanupError) {
          console.warn('Warning: Could not remove workflow URL:', cleanupError);
        }
        
        // Complete the dataset creation flow without polling
        fetchAndUpdateSharepointDatasetSelection(datasetIdRef.current);
        dispatch(updateDatasetState({ listOfUserOwnedDataset: [] }));
        dispatch(refreshDatasetList());
        setDatasetCreated(true);
        return;
      }
      
      let workflowData = prepare.data;
      while (workflowData.status !== 'Completed') {
        await new Promise((r) => setTimeout(r, 5000));
        const workflow = await Api.getWorkflowForDataset(datasetIdRef.current, workflowData._id);
        if (workflow.data) workflowData = workflow.data;
      }

      if (workflowData.status === 'Completed') {
        fetchAndUpdateSharepointDatasetSelection(datasetIdRef.current); // updating the selected dataset by passing the createddataset ID
        dispatch(updateDatasetState({ listOfUserOwnedDataset: [] }));
        dispatch(refreshDatasetList());
        setDatasetCreated(true); // Show dataset details after completion
      }
    } catch (error) {
      console.error('Error in sharePointExecution:', error);
      enqueueSnackbar(t('fileUpload.uploadFileMessage.failureSharepointError'), { variant: 'error' });
    } finally {
      setIsDatasetCreating(false);
      attachDataset(datasetIdRef.current);
    }
  };

  const deleteDataset = async () => {
    try {
      await Api.deleteDataset(datasetIdRef.current);
      setDatasetCreated(false);
      setSiteUrl('');
      setWorkflowUrl('');
      setSharePointDatasetName('');
      datasetIdRef.current = '';
      attachDataset('');
    } catch (error) {
      showSnackbarErrorMessage(error as ApiError);
    }
  };

  return (
    <>
      {!datasetCreated ? (
        <Box>
          <Typography fontSize={16} fontWeight={700} sx={{ marginTop: '1.5rem' }} id="dataset-name">
            {t('fileUpload.enterDocumentName')} <span aria-hidden="true">*</span>
          </Typography>
          <Input
            fullWidth
            id="dataset-name"
            sx={{ padding: '0.2rem 0rem', marginBottom: '1rem' }}
            onChange={(event) => setSharePointDatasetName(event.target.value)}
            disabled={isDatasetCreating}
            inputProps={{
              'aria-labelledby': 'dataset-name',
              'aria-required': 'true',
              'aria-label': `${t('fileUpload.enterDocumentName')} required`,
            }}
          />

          <Typography fontSize={16} fontWeight={700} sx={{ marginTop: '1rem' }} id="site-url">
            {t('fileUpload.enterSiteUrl')} <span aria-hidden="true">*</span>
          </Typography>
          <Input
            fullWidth
            id="site-url"
            sx={{ padding: '0.2rem 0rem' }}
            onChange={(event) => setSiteUrl(event.target.value)}
            disabled={isDatasetCreating}
            inputProps={{
              'aria-labelledby': 'site-url',
              'aria-required': 'true',
              'aria-label': `${t('fileUpload.enterSiteUrl')} required`,
            }}
          />

          <Typography fontSize={16} fontWeight={700} sx={{ marginTop: '1rem' }} id="workflow-url">
            {t('fileUpload.enterWorkflowURL')} <span aria-hidden="true">*</span>
          </Typography>
          <Input
            fullWidth
            id="workflow-url"
            sx={{ padding: '0.2rem 0rem' }}
            onChange={(event) => setWorkflowUrl(event.target.value)}
            disabled={isDatasetCreating}
            inputProps={{
              'aria-labelledby': 'workflow-url',
              'aria-required': 'true',
              'aria-label': `${t('fileUpload.enterSiteUrl')} required`,
            }}
          />



          {!isDatasetCreating && (
            <Button
              sx={{
                float: 'right',
                width: '200px',
                margin: '1rem 0',
                textTransform: 'none',
                backgroundColor: '#3C82F6',
              }}
              onClick={createSharePointDocument}
              variant="contained"
              disabled={!siteUrl || !workflowUrl || !sharePointDatasetName}
            >
              {t('fileUpload.createdataset')}
            </Button>
          )}

          {isDatasetCreating && (
            <Box sx={{ textAlign: 'center', marginTop: '1rem' }}>
              <LinearProgress />
              <Typography sx={{ fontWeight: 700, marginTop: '0.5rem', color: '#3C82F6' }}>
                {t('fileUpload.datasetCreationMessage')}...
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Box
          sx={{ padding: '1rem', borderRadius: '8px', boxShadow: 2, backgroundColor: '#f9f9f9', position: 'relative' }}
        >
          <IconButton onClick={deleteDataset} sx={{ position: 'absolute', top: '10px', right: '10px', color: 'red' }}>
            <DeleteIcon />
          </IconButton>
          <Typography fontSize={18} fontWeight={700} sx={{ marginBottom: '10px' }}>
            {t('fileUpload.datasetDetails')}
          </Typography>
          <Typography fontSize={16} fontWeight={500}>
            <strong>{t('fileUpload.name')}:</strong> {sharePointDatasetName}
          </Typography>
          <Typography fontSize={16} fontWeight={500}>
            <strong>{t('fileUpload.siteUrl')}</strong> {siteUrl}
          </Typography>
          <Typography fontSize={16} fontWeight={500} sx={{ wordBreak: 'break-word' }}>
            <strong>{t('fileUpload.workflowURl')}</strong> {workflowUrl}
          </Typography>
          {selectedSharepointDataset && selectedSharepointDataset.files.length !== 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', margin: '1rem 0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ color: '#636363' }}>{t('fileUpload.datasetContent')}</Typography>

                <Typography>{selectedSharepointDataset.files.length}</Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: '6.5rem',
                  overflowY: 'scroll',
                  gap: '.25rem',
                }}
              >
                {selectedSharepointDataset.files.map((file, key) => (
                  <Typography
                    key={key}
                    sx={{
                      padding: '.25rem .25rem',
                      background: '#F6F6F6B2',
                      borderRadius: '.25rem',
                    }}
                  >
                    {file.name}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </>
  );
}
